import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, withModifiers as _withModifiers, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { class: "text-muted-foreground hover:text-primary cursor-pointer" }
const _hoisted_3 = ["onClick"]

import type { Column, Table } from '@tanstack/vue-table';
    import { computed, onBeforeUnmount, ref, watch } from 'vue';
    import { Button } from '@/components/ui/button';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuLabel,
        DropdownMenuSeparator,
        DropdownMenuTrigger,
    } from '@/components/ui/dropdown-menu';
    import { Eye, GripVertical } from 'lucide-vue-next';
    import draggable from 'vuedraggable';
    import AddButton from '@/components/AddButton.vue';
    import ViewOption from '@/components/ViewOption.vue';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';
    import { useViewOptionsStore } from '@/stores/view-options';
    import { Checkbox } from '@/components/ui/checkbox';
    import { Label } from '@/components/ui/label';

    interface DataTableViewOptionsProps {
        table: Table<any>;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'DataTableAdvancedViewOptions',
  props: {
    table: {}
  },
  setup(__props: any) {

    const props = __props;

    const viewOptionsStore = useViewOptionsStore();

    const columns = computed(() =>
        props.table
            .getAllColumns()
            .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide()),
    );

    const unhideableColumns = computed(() =>
        props.table.getAllColumns().filter((column) => !column.getCanHide()),
    );

    // Helper function to get column header title
    const getColumnTitle = (column: any) => {
        const headerContext = { column };
        const headerContent = column.columnDef.header(headerContext);
        return headerContent?.props?.title || column.id;
    };

    const someColumnsVisible = computed(() => {
        const visibleCount =
            props.table.getVisibleFlatColumns().length - unhideableColumns.value.length;
        return visibleCount > 0 && visibleCount < columns.value.length;
    });

    const getColumn = (columnId: string): Column<any> | undefined =>
        props.table.getAllColumns().find((column) => column.id === columnId);

    // The built-in column showing, captured once. The (virtual) default view
    // restores this; it can't be edited by anyone.
    const builtInColumnVisibility = { ...props.table.getState().columnVisibility };

    // Only the owner of a stored view may change its columns/order. The default
    // view and views shared by other users are read-only.
    const canEdit = computed(
        () =>
            !!viewOptionsStore.activeViewOption?.is_owner &&
            !viewOptionsStore.activeViewOption?.is_default,
    );

    // Non-reorderable columns (e.g. select/id/name) must stay leading in the
    // column order so the reordered hideable columns line up after them.
    const fixedColumnIds = computed(() => unhideableColumns.value.map((column) => column.id));

    // Local order of the reorderable (hideable) columns, edited via drag and drop.
    const localColumnIds = ref<string[]>([]);

    const syncLocalOrder = () => {
        const hideableIds = columns.value.map((column) => column.id);
        const saved = viewOptionsStore.activeViewOption?.column_order ?? [];
        const ordered = saved.filter((id) => hideableIds.includes(id));
        const missing = hideableIds.filter((id) => !ordered.includes(id));
        localColumnIds.value = [...ordered, ...missing];
    };

    const fullColumnOrder = () => [...fixedColumnIds.value, ...localColumnIds.value];

    const applyColumnOrderToTable = () => {
        props.table.setColumnOrder(fullColumnOrder());
    };

    const persistColumnOrder = () => {
        if (!viewOptionsStore.activeViewOption) {
            return;
        }

        viewOptionsStore.updateColumnOrder(
            viewOptionsStore.activeViewOption.id,
            fullColumnOrder(),
        );
    };

    const handleDragEnd = () => {
        if (!canEdit.value) {
            return;
        }

        applyColumnOrderToTable();
        persistColumnOrder();
    };

    const timeout = ref<ReturnType<typeof setTimeout>>();

    const debouncedSaveColumnVisibility = () => {
        if (timeout.value) {
            clearTimeout(timeout.value);
        }

        timeout.value = setTimeout(() => {
            if (viewOptionsStore.activeViewOption) {
                const columnVisibility = props.table.getAllColumns().reduce(
                    (acc, column) => {
                        acc[column.id] = column.getIsVisible();
                        return acc;
                    },
                    {} as Record<string, boolean>,
                );

                viewOptionsStore.updateColumnVisibility(
                    viewOptionsStore.activeViewOption.id,
                    columnVisibility,
                );
            }
        }, 500);
    };

    const handleColumnVisibilityChange = (column: any, value: boolean) => {
        column.toggleVisibility(!!value);
        debouncedSaveColumnVisibility();
    };

    const handleColumnRowClick = (columnId: string) => {
        if (viewOptionsStore.editState || !canEdit.value) {
            return;
        }

        const column = getColumn(columnId);

        if (column) {
            handleColumnVisibilityChange(column, !column.getIsVisible());
        }
    };

    const handleToggleAllColumns = (value: boolean | 'indeterminate') => {
        if (!canEdit.value) {
            return;
        }

        const handler = props.table.getToggleAllColumnsVisibilityHandler();
        handler(value);
        debouncedSaveColumnVisibility();
    };

    const { isPending, isError, error, isSuccess, mutate } = useMutation({
        mutationFn: (newComment: {
            name: string;
            table_identifier: string;
            column_visibility: Record<string, boolean>;
            column_order: string[];
        }) => axios.post(route('v2.dashboard.view-options.store'), newComment),
        onSuccess: (data) => {
            viewOptionsStore.addViewOption(data.data);
            viewOptionsStore.setState('edit');
        },
    });

    function addViewOption() {
        mutate({
            name: '',
            table_identifier: 'employee-dashboard',
            column_visibility: props.table.getAllColumns().reduce(
                (acc, column) => {
                    acc[column.id] = column.getIsVisible();
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
            column_order: fullColumnOrder(),
        });
    }

    watch(
        () => viewOptionsStore.activeViewOption,
        (newOption) => {
            if (!newOption) {
                return;
            }

            const visibility = newOption.is_default
                ? { ...builtInColumnVisibility }
                : (newOption.column_visibility ?? {});

            props.table.setColumnVisibility(visibility);
        },
        { immediate: true },
    );

    watch(
        () => viewOptionsStore.activeViewOption?.id,
        () => {
            syncLocalOrder();
            applyColumnOrderToTable();
        },
        { immediate: true },
    );

    onBeforeUnmount(() => clearTimeout(timeout.value));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(DropdownMenu), null, {
    default: _withCtx(() => [
      _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
        default: _withCtx(() => [
          _createVNode(_unref(Button), {
            variant: "outline",
            size: "xs",
            class: "normal-case"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Eye)),
              (!_unref(viewOptionsStore).activeViewOption?.is_default)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(_toDisplayString(_unref(viewOptionsStore).activeViewOption?.name), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(DropdownMenuContent), {
        align: "end",
        class: "min-w-xs"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DropdownMenuLabel), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('datatables.view_options.global_views')), 1)
            ]),
            _: 1
          }),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(viewOptionsStore).globalViewOptions, (viewOption) => {
            return (_openBlock(), _createBlock(ViewOption, {
              key: viewOption.id,
              "view-option": viewOption,
              onKeydown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
            }, null, 8, ["view-option"]))
          }), 128)),
          (_unref(viewOptionsStore).personalViewOptions.length)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(DropdownMenuSeparator), { class: "mx-2" }),
                _createVNode(_unref(DropdownMenuLabel), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('datatables.view_options.personal_views')), 1)
                  ]),
                  _: 1
                }),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(viewOptionsStore).personalViewOptions, (viewOption) => {
                  return (_openBlock(), _createBlock(ViewOption, {
                    key: viewOption.id,
                    "view-option": viewOption,
                    onKeydown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                  }, null, 8, ["view-option"]))
                }), 128))
              ], 64))
            : _createCommentVNode("", true),
          _createVNode(AddButton, {
            onClick: addViewOption,
            class: "w-full"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('datatables.view_options.add_option')), 1)
            ]),
            _: 1
          }),
          (_unref(viewOptionsStore).activeViewOption)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(DropdownMenuLabel), { class: "text-muted-foreground mt-2 font-normal" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('datatables.view_options.choose_columns')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DropdownMenuSeparator), { class: "mx-2" }),
                (canEdit.value)
                  ? (_openBlock(), _createBlock(_unref(Label), {
                      key: 0,
                      class: "flex flex-row items-center gap-2 px-2 py-1.5 font-normal"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Checkbox), {
                          "model-value": 
                            _ctx.table.getIsAllColumnsVisible()
                                ? true
                                : someColumnsVisible.value
                                  ? 'indeterminate'
                                  : false
                        ,
                          "onUpdate:modelValue": handleToggleAllColumns,
                          "aria-label": "Toggle all columns"
                        }, null, 8, ["model-value"]),
                        _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.table.getIsAllColumnsVisible()
                                ? _ctx.$t('forms.unselect_all')
                                : _ctx.$t('forms.select_all')), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_unref(draggable), {
                  modelValue: localColumnIds.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localColumnIds).value = $event)),
                  "item-key": "columnId",
                  handle: ".column-drag-handle",
                  disabled: !canEdit.value,
                  animation: 150,
                  "ghost-class": "opacity-50",
                  tag: "div",
                  onEnd: handleDragEnd
                }, {
                  item: _withCtx(({ element: columnId }) => [
                    (getColumn(columnId))
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: _normalizeClass(["group flex flex-row items-stretch text-xs select-none", {
                                'pointer-events-none opacity-50': _unref(viewOptionsStore).editState,
                            }])
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["flex min-h-8 flex-1 items-center gap-2 py-1.5 pl-2", canEdit.value ? 'cursor-pointer' : 'cursor-default']),
                            onClick: ($event: any) => (handleColumnRowClick(columnId))
                          }, [
                            _createVNode(_unref(Checkbox), {
                              "model-value": getColumn(columnId)?.getIsVisible(),
                              disabled: !canEdit.value,
                              class: _normalizeClass(["pointer-events-none", { 'grayscale opacity-60': !canEdit.value }]),
                              "aria-hidden": "true",
                              tabindex: "-1"
                            }, null, 8, ["model-value", "disabled", "class"]),
                            _createElementVNode("span", {
                              class: _normalizeClass(["text-muted-foreground", { 'group-hover:text-primary': canEdit.value }])
                            }, _toDisplayString(_ctx.$t(getColumnTitle(getColumn(columnId)))), 3)
                          ], 10, _hoisted_3),
                          (canEdit.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "column-drag-handle text-muted-foreground group-hover:text-primary flex shrink-0 cursor-grab items-center self-stretch px-2 active:cursor-grabbing",
                                onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                              }, [
                                _createVNode(_unref(GripVertical), { class: "size-3.5" })
                              ]))
                            : _createCommentVNode("", true)
                        ], 2))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["modelValue", "disabled"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})